import {
  fetchEthBlock,
  fetchEthLatest,
  fetchSlot2HistoryRecent,
} from './api/slot2Client';
import {
  easeInOutQuint,
  Slot2FullViewCanvas,
  type CanvasTopModel,
} from './slot2FullViewCanvas';

declare global {
  interface Window {
    /** file 빌드/임베드에서 런타임에 주입하는 WS URL (app.js 보다 먼저 설정) */
    __SLOT2_WS_URL__?: string;
    /** 레거시 번들 호환: window.SLOT2_WS_URL 을 먼저 보던 빌드가 존재 */
    SLOT2_WS_URL?: string;
  }
}

const SPIN_DURATION_MS = 7800;

function envForceHttpEthSync(): boolean {
  return (
    import.meta.env.VITE_SLOT2_FORCE_HTTP_SYNC === '1' ||
    import.meta.env.VITE_SLOT2_FORCE_HTTP_SYNC === 'true'
  );
}

function wsDisabled(): boolean {
  return (
    import.meta.env.VITE_SLOT2_DISABLE_WS === '1' ||
    import.meta.env.VITE_SLOT2_DISABLE_WS === 'true'
  );
}

function resolveSlot2WsUrl(): string | null {
  if (wsDisabled()) return null;
  const injected =
    typeof window !== 'undefined'
      ? String(window.__SLOT2_WS_URL__ ?? window.SLOT2_WS_URL ?? '').trim()
      : '';
  if (injected) return injected;
  const u = import.meta.env.VITE_SLOT2_WS_URL?.trim();
  if (u === '0' || u?.toLowerCase() === 'off') return null;
  if (u) return u;
  const proto = globalThis.location?.protocol === 'https:' ? 'wss' : 'ws';
  const host = globalThis.location?.hostname || 'localhost';
  return `${proto}://${host}:8081`;
}

type Side = 'red' | 'green';

interface OnchainRow {
  round: number;
  result: Side;
  randomnessHex: string;
}

interface HistoryStripItem extends Record<string, unknown> {
  round: string;
  round_id: number;
  display_round: number;
  result: Side;
}

function escapeHtml(s: unknown): string {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[ch] || ch;
  });
}

function shortenHex(h: string): string {
  h = String(h || '');
  if (h.length <= 18) return h;
  return `${h.slice(0, 10)}…${h.slice(-6)}`;
}

function toHexNibble(ch: string): number | null {
  const c = String(ch || '').toLowerCase();
  if (c >= '0' && c <= '9') return c.charCodeAt(0) - 48;
  if (c >= 'a' && c <= 'f') return c.charCodeAt(0) - 87;
  return null;
}

function setText(el: HTMLElement | null, v: string) {
  if (el) el.textContent = v;
}

export function initSlot2Game(): void {
  const slot2Panel = document.getElementById('cr-game-slot2');
  if (!slot2Panel || !slot2Panel.classList.contains('active')) return;
  document.body.classList.add('d2-active');

  const c0 = document.getElementById('s2-page-canvas') as HTMLCanvasElement | null;
  const r0 = document.getElementById('s2-rand-input') as HTMLInputElement | null;
  const tabShell = document.getElementById('s2-tab-shell');
  const tabHistory = document.getElementById('s2-tab-history');
  const tabVerify = document.getElementById('s2-tab-verify');
  const dropHistory = document.getElementById('s2-dropdown-history');
  const dropVerify = document.getElementById('s2-dropdown-verify');
  if (!c0 || !r0 || !tabShell || !tabHistory || !tabVerify || !dropHistory || !dropVerify) return;

  const canvasEl: HTMLCanvasElement = c0;
  const randInputEl: HTMLInputElement = r0;

  const fv = new Slot2FullViewCanvas(canvasEl);

  let lastSpinRoundId: number | null = null;

  let wsChainSyncConnected = false;
  function useHttpForEthChain(): boolean {
    if (envForceHttpEthSync()) return true;
    if (resolveSlot2WsUrl() == null) return true;
    return !wsChainSyncConnected;
  }

  let countdown = 0;
  /** 서버 브로드캐스트 `gameInterval` 로 덮어씀 — WS 없을 때만 이 기본값 사용 */
  let gameIntervalSec = 13;

  function applyGameIntervalFromServer(raw: unknown) {
    if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 5 && raw <= 120) {
      gameIntervalSec = Math.floor(raw);
    }
  }
  const S2_START_BASE = 0;
  let s2GameActivated = false;

  let s2DisplayedRound: number | null = null;
  const s2HistoryList: HistoryStripItem[] = [];
  let s2LastFetchedEven: number | null = null;
  let s2NewestRound: number | null = null;

  let wheelRotation = 0;
  let wheelDisplayAngle = 0;
  let spinAnim: { start: number; from: number; to: number } | null = null;
  const TARGET_DEG: Record<Side, number> = { red: 78.75, green: 56.25 };

  let activeTab: null | 'history' | 'verify' = null;

  const histDrop = dropHistory;
  const verDrop = dropVerify;
  const histTab = tabHistory;
  const verTab = tabVerify;

  function closeDropdowns(skipLayoutDraw?: boolean) {
    activeTab = null;
    histDrop.hidden = true;
    verDrop.hidden = true;
    histTab.classList.remove('is-active');
    verTab.classList.remove('is-active');
    histTab.setAttribute('aria-expanded', 'false');
    verTab.setAttribute('aria-expanded', 'false');
    if (!skipLayoutDraw) {
      requestAnimationFrame(() => {
        remeasureTopInset();
        flushDraw();
      });
    }
  }

  function openPanel(which: 'history' | 'verify') {
    if (activeTab === which) {
      closeDropdowns();
      return;
    }
    closeDropdowns(true);
    activeTab = which;
    if (which === 'history') {
      histDrop.hidden = false;
      histTab.classList.add('is-active');
      histTab.setAttribute('aria-expanded', 'true');
    } else {
      verDrop.hidden = false;
      verTab.classList.add('is-active');
      verTab.setAttribute('aria-expanded', 'true');
    }
    requestAnimationFrame(() => {
      remeasureTopInset();
      flushDraw();
    });
  }

  tabHistory.addEventListener('click', (e) => {
    e.stopPropagation();
    openPanel('history');
  });
  tabVerify.addEventListener('click', (e) => {
    e.stopPropagation();
    openPanel('verify');
  });

  document.addEventListener(
    'pointerdown',
    (e) => {
      const t = e.target as Node;
      if (tabShell.contains(t) || canvasEl.contains(t)) return;
      closeDropdowns();
    },
    true
  );

  let cachedLogoRowH = 52;
  function buildTopModel(topInsetPx: number): CanvasTopModel {
    return {
      blockStr: s2DisplayedRound != null ? String(s2DisplayedRound) : '-',
      timerStr: s2GameActivated ? String(Math.max(0, countdown)) : '0',
      wheelAngleDeg: wheelDisplayAngle,
      topInsetPx,
      logoRowH: cachedLogoRowH,
      blockRowDimmed: spinAnim != null,
    };
  }

  /** 캔버스와 동일 기준(#cr-game-slot2)에서 탭 셸 위치·높이 — 로고 행 정렬 */
  let cachedTopInsetPx = 48;
  function remeasureTopInset() {
    if (!tabShell) {
      cachedTopInsetPx = 48;
      cachedLogoRowH = 52;
      return;
    }
    cachedTopInsetPx = Math.max(0, Math.round(tabShell.offsetTop));
    cachedLogoRowH = Math.max(40, Math.round(tabShell.offsetHeight));
  }

  function flushDraw() {
    fv.draw(buildTopModel(cachedTopInsetPx));
  }
  fv.setInvalidate(flushDraw);

  function tickSpin() {
    if (!spinAnim) return;
    const t = (performance.now() - spinAnim.start) / SPIN_DURATION_MS;
    if (t >= 1) {
      wheelDisplayAngle = spinAnim.to;
      spinAnim = null;
      startPostSpinCountdown();
      return;
    }
    wheelDisplayAngle =
      spinAnim.from + (spinAnim.to - spinAnim.from) * easeInOutQuint(t);
    flushDraw();
    requestAnimationFrame(tickSpin);
  }

  function spinS2Wheel(result: Side) {
    countdown = 0;
    const baseDeg = TARGET_DEG[result] ?? 0;
    const current = wheelRotation % 360;
    const diff = (baseDeg - current + 360) % 360;
    wheelRotation += 7 * 360 + diff;
    spinAnim = {
      start: performance.now(),
      from: wheelDisplayAngle,
      to: wheelRotation,
    };
    tickSpin();
  }

  function setS2Block(round: number | string) {
    const n = typeof round === 'number' ? round : parseInt(String(round), 10) || 0;
    if (!n) return;
    s2DisplayedRound = n;
    flushDraw();
  }

  /** 룰렛 스핀 애니메이션 종료 후 다음 라운드까지 남은 시간(초) */
  function startPostSpinCountdown() {
    countdown = gameIntervalSec;
    flushDraw();
  }

  function renderS2History(_history: Array<Record<string, unknown>>) {
    /* 스트립 미사용 */
  }

  let s2ToastTimer: ReturnType<typeof setTimeout> | null = null;
  function showS2Toast(msg: string) {
    try {
      const old = document.getElementById('s2-copied-toast');
      if (old?.parentNode) old.parentNode.removeChild(old);
      const t = document.createElement('div');
      t.id = 's2-copied-toast';
      t.className = 's2-copied-toast';
      t.textContent = msg || '복사 완료';
      document.body.appendChild(t);
      if (s2ToastTimer) clearTimeout(s2ToastTimer);
      s2ToastTimer = setTimeout(() => {
        if (t.parentNode) t.parentNode.removeChild(t);
      }, 1200);
    } catch {
      /* ignore */
    }
  }

  function copyText(text: string, toastMsg?: string) {
    const tx = String(text || '');
    if (!tx) return;
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(tx).then(
        () => showS2Toast(toastMsg || '복사 완료'),
        () => fallbackCopy(tx, toastMsg)
      );
    } else {
      fallbackCopy(tx, toastMsg);
    }
  }

  function fallbackCopy(text: string, toastMsg?: string) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showS2Toast(toastMsg || '복사 완료');
    } catch {
      /* ignore */
    }
  }

  const rVerifyRound = document.getElementById('s2-verify-round');
  const aEtherscan = document.getElementById('s2-etherscan-link') as HTMLAnchorElement | null;
  const last4HexEl = document.getElementById('s2-last4-hex');
  const last4DecEl = document.getElementById('s2-last4-dec');
  const parityEl = document.getElementById('s2-last4-parity');
  const stepsEl = document.getElementById('s2-verify-steps');

  function updateVerifyLink(round: number | null) {
    setText(rVerifyRound, round != null ? String(round) : '-');
    if (aEtherscan) {
      if (round != null) {
        aEtherscan.href = `https://etherscan.io/block/${encodeURIComponent(String(round))}`;
        aEtherscan.style.pointerEvents = '';
        aEtherscan.style.opacity = '';
      } else {
        aEtherscan.href = '#';
        aEtherscan.style.pointerEvents = 'none';
        aEtherscan.style.opacity = '0.5';
      }
    }
  }

  function renderVerifyCalc(randomnessHex?: string) {
    const raw = String(randomnessHex ?? randInputEl.value ?? '').trim();
    if (randomnessHex != null) randInputEl.value = raw;

    const clean = raw.replace(/^0x/i, '').replace(/\s+/g, '');
    if (!clean || clean.length < 4) {
      setText(last4HexEl, '-');
      setText(last4DecEl, '-');
      setText(parityEl, '-');
      setText(stepsEl, '-');
      flushDraw();
      return;
    }

    const last4 = clean.slice(-4).toLowerCase();
    const n0 = toHexNibble(last4[0]!);
    const n1 = toHexNibble(last4[1]!);
    const n2 = toHexNibble(last4[2]!);
    const n3 = toHexNibble(last4[3]!);
    if (n0 == null || n1 == null || n2 == null || n3 == null) {
      setText(last4HexEl, '-');
      setText(last4DecEl, '-');
      setText(parityEl, '-');
      setText(stepsEl, '입력값이 올바른 16진수(hex)가 아닙니다.');
      flushDraw();
      return;
    }

    const dec = n0 * 4096 + n1 * 256 + n2 * 16 + n3;
    const isOdd = dec % 2 === 1;
    const resultLabel = isOdd ? '🍒 체리 (홀)' : '🍉 수박 (짝)';

    setText(last4HexEl, `0x${last4}`);
    setText(last4DecEl, String(dec));
    setText(parityEl, resultLabel);

    if (stepsEl) {
      stepsEl.textContent = [
        `마지막 4자리: ${last4}`,
        `${last4[0]}→${n0}, ${last4[1]}→${n1}, ${last4[2]}→${n2}, ${last4[3]}→${n3}`,
        `계산: ${n0}×4096 + ${n1}×256 + ${n2}×16 + ${n3}×1`,
        `= ${n0 * 4096} + ${n1 * 256} + ${n2 * 16} + ${n3}`,
        `= ${dec}`,
        `판정: ${dec} % 2 = ${dec % 2} → ${isOdd ? '홀(체리)' : '짝(수박)'}`,
      ].join('\n');
    }

    flushDraw();
  }

  const s2OnchainCache: Record<number, OnchainRow> = {};
  const s2OnchainPerPage = 100;
  const s2OnchainPageSize = 10;
  let s2OnchainPage = 1;
  let s2OnchainRows: OnchainRow[] = [];

  async function fetchS2BlockResult(blockNum: number): Promise<OnchainRow | null> {
    if (!blockNum) return null;
    if (s2OnchainCache[blockNum]) return s2OnchainCache[blockNum]!;
    const d = await fetchEthBlock(blockNum);
    if (!d || d.error) return null;
    const isOdd =
      d.isOdd === true ||
      d.parityValue === 1 ||
      String(d.parityValue ?? '') === '1';
    const result: Side = isOdd ? 'red' : 'green';
    const row: OnchainRow = {
      round: blockNum,
      result,
      randomnessHex: d.randomnessHex || '',
    };
    s2OnchainCache[blockNum] = row;
    return row;
  }

  const resultsEl = document.getElementById('s2-today-results');
  const pagEl = document.getElementById('s2-today-pagination');

  function renderS2OnchainResults(rows: OnchainRow[]) {
    if (!resultsEl) return;
    if (!rows?.length) {
      resultsEl.innerHTML = '<p class="cr-empty-msg">블록 생성 기다리는 중입니다.</p>';
      flushDraw();
      return;
    }

    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / s2OnchainPageSize));
    if (s2OnchainPage > totalPages) s2OnchainPage = totalPages;
    if (s2OnchainPage < 1) s2OnchainPage = 1;
    const start = (s2OnchainPage - 1) * s2OnchainPageSize;
    const pageRows = rows.slice(start, start + s2OnchainPageSize);

    let html =
      '<table class="cr-today-table"><thead><tr><th>회차(블록)</th><th>결과</th><th>Block Randomness (클릭 복사)</th></tr></thead><tbody>';
    pageRows.forEach((r) => {
      const icon = r.result === 'red' ? '🍒' : '🍉';
      const label = r.result === 'red' ? '체리' : '수박';
      const rnd = escapeHtml(r.randomnessHex || '-');
      const rowClass =
        's2-onchain-row' + (s2NewestRound != null && r.round === s2NewestRound ? ' s2-row-new' : '');
      html += `<tr class="${rowClass}" data-round="${escapeHtml(r.round)}" data-rand="${rnd}">`;
      html += `<td>${escapeHtml(r.round)}</td><td>${icon} ${label}</td>`;
      html += `<td><code class="s2-copyable" data-copy="${rnd}" title="클릭하면 복사됩니다">${escapeHtml(shortenHex(rnd))}</code></td></tr>`;
    });
    html += '</tbody></table>';
    resultsEl.innerHTML = html;

    if (s2NewestRound != null) {
      setTimeout(() => {
        s2NewestRound = null;
      }, 800);
    }

    resultsEl.querySelectorAll('tr.s2-onchain-row').forEach((tr) => {
      tr.addEventListener('click', function (this: HTMLTableRowElement) {
        const round = this.getAttribute('data-round');
        const rand = this.getAttribute('data-rand');
        if (round) updateVerifyLink(parseInt(round, 10) || null);
        if (rand) renderVerifyCalc(rand);
      });
    });

    resultsEl.querySelectorAll('[data-copy]').forEach((node) => {
      node.addEventListener('click', (e) => {
        e.stopPropagation();
        const txt = (e.currentTarget as HTMLElement).getAttribute('data-copy') || '';
        if (txt && txt !== '-') copyText(txt, 'Block Randomness 복사 완료');
      });
    });

    flushDraw();
  }

  function renderS2OnchainPagination() {
    if (!pagEl) return;
    const total = s2OnchainRows.length;
    if (!total) {
      pagEl.innerHTML = '';
      return;
    }

    const totalPages = Math.max(1, Math.ceil(total / s2OnchainPageSize));
    if (s2OnchainPage > totalPages) s2OnchainPage = totalPages;
    if (s2OnchainPage < 1) s2OnchainPage = 1;

    let html = '';
    html += `<button type="button" data-s2-page="prev"${s2OnchainPage <= 1 ? ' disabled' : ''}>이전</button>`;
    html += `<span class="cr-page-info">${s2OnchainPage} / ${totalPages}</span>`;
    html += `<button type="button" data-s2-page="next"${s2OnchainPage >= totalPages ? ' disabled' : ''}>다음</button>`;
    pagEl.innerHTML = html;

    pagEl.querySelector('[data-s2-page="prev"]')?.addEventListener('click', () => {
      if (s2OnchainPage <= 1) return;
      s2OnchainPage--;
      renderS2OnchainResults(s2OnchainRows);
      renderS2OnchainPagination();
    });
    pagEl.querySelector('[data-s2-page="next"]')?.addEventListener('click', () => {
      const tp = Math.max(1, Math.ceil(s2OnchainRows.length / s2OnchainPageSize));
      if (s2OnchainPage >= tp) return;
      s2OnchainPage++;
      renderS2OnchainResults(s2OnchainRows);
      renderS2OnchainPagination();
    });
  }

  function applyStatusFromServer(st: Record<string, unknown> | null | undefined) {
    if (!st || typeof st !== 'object') return;
    const blockNumberDec =
      typeof st.blockNumberDec === 'number' ? st.blockNumberDec : null;
    const prevEvenBlock =
      typeof st.prevEvenBlock === 'number' ? st.prevEvenBlock : null;
    const displayRound =
      typeof st.displayRound === 'number' ? st.displayRound : null;
    const rowsRaw = st.rows;
    if (blockNumberDec == null || prevEvenBlock == null || displayRound == null) return;

    applyGameIntervalFromServer(st.game_interval);
    applyGameIntervalFromServer(st.gameInterval);

    updateVerifyLink(prevEvenBlock);

    if (!s2GameActivated) {
      if (S2_START_BASE && prevEvenBlock < S2_START_BASE) return;
      s2GameActivated = true;
    }

    let displayRoundAdvanced = false;
    if (s2DisplayedRound == null || s2DisplayedRound < displayRound) {
      displayRoundAdvanced = true;
      setS2Block(displayRound);
    } else if (s2DisplayedRound !== displayRound) {
      setS2Block(displayRound);
    }

    if (!Array.isArray(rowsRaw)) {
      if (displayRoundAdvanced) startPostSpinCountdown();
      return;
    }

    const next: OnchainRow[] = [];
    for (const x of rowsRaw) {
      if (!x || typeof x !== 'object') continue;
      const o = x as Record<string, unknown>;
      const r = Number(o.round);
      if (!Number.isFinite(r)) continue;
      const result: Side = o.result === 'red' ? 'red' : 'green';
      next.push({
        round: r,
        result,
        randomnessHex: String(o.randomnessHex || ''),
      });
    }
    next.sort((a, b) => b.round - a.round);
    s2OnchainRows = next.slice(0, s2OnchainPerPage);
    if (s2OnchainRows.length) {
      s2LastFetchedEven = s2OnchainRows[0]!.round;
    }

    renderS2OnchainResults(s2OnchainRows);
    renderS2OnchainPagination();

    const latestRow = s2OnchainRows[0];
    if (latestRow?.randomnessHex) {
      renderVerifyCalc(latestRow.randomnessHex);
    }

    if (latestRow && latestRow.round !== lastSpinRoundId) {
      const resultSide: Side = latestRow.result === 'red' ? 'red' : 'green';
      s2HistoryList.push({
        round: String(latestRow.round),
        round_id: latestRow.round,
        display_round: latestRow.round,
        result: resultSide,
      });
      if (s2HistoryList.length > 20) {
        s2HistoryList.splice(0, s2HistoryList.length - 20);
      }
      renderS2History(s2HistoryList);
      spinS2Wheel(resultSide);
      lastSpinRoundId = latestRow.round;
    } else if (displayRoundAdvanced) {
      startPostSpinCountdown();
    }
  }

  function mergeHistoryItemsFromHello(items: Array<Record<string, unknown>> | undefined) {
    if (!items?.length) return;
    const byRound: Record<number, OnchainRow> = {};
    s2OnchainRows.forEach((r) => {
      byRound[r.round] = r;
    });
    for (const it of items) {
      const r = Number(it.round);
      if (!Number.isFinite(r)) continue;
      if (byRound[r]) continue;
      byRound[r] = {
        round: r,
        result: it.result === 'red' ? 'red' : 'green',
        randomnessHex: String(it.randomnessHex || it.randomness_hex || ''),
      };
    }
    s2OnchainRows = Object.keys(byRound)
      .map(Number)
      .sort((a, b) => b - a)
      .map((k) => byRound[k]!)
      .slice(0, s2OnchainPerPage);
    if (s2OnchainRows.length) {
      s2LastFetchedEven = s2OnchainRows[0]!.round;
    }
    s2OnchainPage = 1;
    renderS2OnchainResults(s2OnchainRows);
    renderS2OnchainPagination();
  }

  const wsRenderDelayMs = 800;

  function applyNewBlockPush(msg: Record<string, unknown>) {
    applyGameIntervalFromServer(msg.game_interval);
    applyGameIntervalFromServer(msg.gameInterval);

    const round = typeof msg.round === 'number' && Number.isFinite(msg.round) ? msg.round : 0;
    if (!round) return;
    if (s2OnchainRows.some((r) => r.round === round)) return;

    const result: Side = msg.result === 'red' ? 'red' : 'green';
    const randHex =
      typeof msg.randomnessHex === 'string' ? msg.randomnessHex : '';
    const row: OnchainRow = {
      round,
      result,
      randomnessHex: randHex,
    };

    s2OnchainRows.unshift(row);
    s2OnchainRows.sort((a, b) => b.round - a.round);
    if (s2OnchainRows.length > s2OnchainPerPage) {
      s2OnchainRows = s2OnchainRows.slice(0, s2OnchainPerPage);
    }

    s2NewestRound = round;
    setTimeout(() => {
      renderS2OnchainResults(s2OnchainRows);
      renderS2OnchainPagination();
    }, wsRenderDelayMs);

    if (s2LastFetchedEven == null || round > s2LastFetchedEven) {
      s2LastFetchedEven = round;
    }
    if (row.randomnessHex) renderVerifyCalc(row.randomnessHex);

    if (round !== lastSpinRoundId) {
      s2HistoryList.push({
        round: String(round),
        round_id: round,
        display_round: round,
        result,
      });
      if (s2HistoryList.length > 20) {
        s2HistoryList.splice(0, s2HistoryList.length - 20);
      }
      renderS2History(s2HistoryList);
      spinS2Wheel(result);
      lastSpinRoundId = round;
    }
  }

  function initS2WebSocket() {
    const wsResolved = resolveSlot2WsUrl();
    if (!wsResolved) return;
    const wsConnectUrl: string = wsResolved;

    const reconnectDelay = 5000;
    let socket: WebSocket | null = null;

    function connect() {
      try {
        socket = new WebSocket(wsConnectUrl);
      } catch {
        return;
      }

      socket.addEventListener('open', () => {
        wsChainSyncConnected = true;
      });

      socket.addEventListener('message', (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as Record<string, unknown>;
          if (!msg?.type) return;

          if (msg.type === 'slot2_hello') {
            if (msg.status && typeof msg.status === 'object') {
              applyStatusFromServer(msg.status as Record<string, unknown>);
            } else if (Array.isArray(msg.history)) {
              mergeHistoryItemsFromHello(msg.history as Array<Record<string, unknown>>);
            }
            return;
          }

          if (msg.type === 'slot2_status') {
            applyStatusFromServer(msg);
            return;
          }

          if (msg.type === 'slot2_new_block') {
            applyNewBlockPush(msg);
          }
        } catch {
          /* ignore */
        }
      });

      socket.addEventListener('close', () => {
        wsChainSyncConnected = false;
        setTimeout(connect, reconnectDelay);
      });

      socket.addEventListener('error', () => {
        try {
          socket?.close();
        } catch {
          /* ignore */
        }
      });
    }

    connect();
  }

  async function loadS2HistoryFromServer() {
    const res = await fetchSlot2HistoryRecent(s2OnchainPerPage);
    if (
      !res ||
      !(res.success === true || res.success === 1 || res.success === '1') ||
      !Array.isArray(res.items)
    ) {
      return;
    }

    const serverRows: OnchainRow[] = res.items.map((it) => ({
      round: it.round,
      result: (it.result === 'red' ? 'red' : 'green') as Side,
      randomnessHex: it.randomnessHex || it.randomness_hex || '',
    }));
    if (!serverRows.length) return;

    const byRound: Record<number, OnchainRow> = {};
    s2OnchainRows.forEach((r) => {
      byRound[r.round] = r;
    });
    serverRows.forEach((r) => {
      if (!byRound[r.round]) byRound[r.round] = r;
    });
    s2OnchainRows = Object.keys(byRound)
      .map(Number)
      .sort((a, b) => b - a)
      .map((round) => byRound[round]!);

    if (s2OnchainRows.length > s2OnchainPerPage) {
      s2OnchainRows = s2OnchainRows.slice(0, s2OnchainPerPage);
    }

    const maxRound = s2OnchainRows.length ? s2OnchainRows[0]!.round : null;
    if (maxRound != null && (s2LastFetchedEven == null || maxRound > s2LastFetchedEven)) {
      s2LastFetchedEven = maxRound;
    }

    s2OnchainPage = 1;
    renderS2OnchainResults(s2OnchainRows);
    renderS2OnchainPagination();
  }

  async function applyBlockFromApi(d: Record<string, unknown> | null) {
    if (d?.error) return;
    const blockNum =
      typeof d?.blockNumberDec === 'number'
        ? d.blockNumberDec
        : d?.blockNumberHex
          ? parseInt(String(d.blockNumberHex).replace(/^0x/i, ''), 16)
          : null;
    if (blockNum == null) return;

    const prevEven = blockNum % 2 === 0 ? blockNum : blockNum - 1;
    if (prevEven <= 0) return;

    const desiredTarget = prevEven + 2;
    updateVerifyLink(prevEven);

    if (!s2GameActivated) {
      if (S2_START_BASE && prevEven < S2_START_BASE) return;
      s2GameActivated = true;
    }
    let targetAdvanced = false;
    if (s2DisplayedRound == null || s2DisplayedRound < desiredTarget) {
      targetAdvanced = true;
      setS2Block(desiredTarget);
    }

    let maxKnown = s2LastFetchedEven;
    if (s2OnchainRows.length && s2OnchainRows[0]?.round != null) {
      if (maxKnown == null || s2OnchainRows[0]!.round > maxKnown) {
        maxKnown = s2OnchainRows[0]!.round;
      }
    }
    let startEven = maxKnown != null ? maxKnown + 2 : prevEven;
    if (startEven > prevEven) startEven = prevEven;

    const blocksToFetch: number[] = [];
    for (let b = startEven; b <= prevEven; b += 2) {
      blocksToFetch.push(b);
    }

    try {
      const rows = await Promise.all(blocksToFetch.map((bn) => fetchS2BlockResult(bn)));
      let latestEvenWithResult: number | null = null;
      let addedAny = false;

      rows.forEach((row) => {
        if (!row) return;
        const rn = row.round;
        const exists = s2OnchainRows.some((r) => r.round === rn);
        if (!exists) {
          s2OnchainRows.unshift(row);
          addedAny = true;
        }
        if (latestEvenWithResult === null || rn > latestEvenWithResult) {
          latestEvenWithResult = rn;
        }
      });

      if (latestEvenWithResult !== null) {
        s2OnchainRows.sort((a, b) => b.round - a.round);
        if (s2OnchainRows.length > s2OnchainPerPage) {
          s2OnchainRows = s2OnchainRows.slice(0, s2OnchainPerPage);
        }
        if (addedAny) s2NewestRound = latestEvenWithResult;

        renderS2OnchainResults(s2OnchainRows);
        renderS2OnchainPagination();

        const latestRow = s2OnchainRows[0]!;
        const resultSide: Side = latestRow.result === 'red' ? 'red' : 'green';
        s2LastFetchedEven = latestEvenWithResult;

        if (latestRow.randomnessHex) {
          renderVerifyCalc(latestRow.randomnessHex);
        }

        const round = latestRow.round;
        if (round !== lastSpinRoundId) {
          s2HistoryList.push({
            round: String(round),
            round_id: round,
            display_round: round,
            result: resultSide,
          });
          if (s2HistoryList.length > 20) s2HistoryList.splice(0, s2HistoryList.length - 20);
          renderS2History(s2HistoryList);

          spinS2Wheel(resultSide);
          lastSpinRoundId = round;
        } else if (targetAdvanced) {
          startPostSpinCountdown();
        }
      } else if (targetAdvanced) {
        startPostSpinCountdown();
      }
    } catch {
      /* 다음 폴링에서 재시도 */
    }
  }

  document.getElementById('s2-rand-demo')?.addEventListener('click', () => {
    renderVerifyCalc('0x8ca11c3a116a2f4e41cd55dc79095ece8b3476fc8eaf85c2ec1d9d1a3015dadf');
  });
  document.getElementById('s2-rand-clear')?.addEventListener('click', () => {
    randInputEl.value = '';
    renderVerifyCalc('');
  });
  randInputEl.addEventListener('input', () => renderVerifyCalc());

  new ResizeObserver(() => {
    remeasureTopInset();
    flushDraw();
  }).observe(slot2Panel);

  initS2WebSocket();

  if (useHttpForEthChain() && !s2OnchainRows.length) {
    void loadS2HistoryFromServer();
  }

  async function pollLatest() {
    const d = await fetchEthLatest();
    const payload = d as unknown as Record<string, unknown> | null;
    await applyBlockFromApi(payload);
  }

  if (useHttpForEthChain()) {
    void pollLatest();
  }
  setInterval(() => {
    if (useHttpForEthChain()) {
      void pollLatest();
    }
  }, 3000);

  setInterval(() => {
    if (!s2GameActivated) {
      countdown = 0;
      flushDraw();
      return;
    }
    if (countdown > 0) {
      countdown--;
      if (countdown < 0) countdown = 0;
    }
    if (countdown <= 0) countdown = 0;
    /* 스핀 중에는 rAF(tickSpin)가 매 프레임 그리므로 초당 중복 전면 리페인트 방지 */
    if (spinAnim == null) {
      flushDraw();
    }
  }, 1000);

  try {
    if (window.localStorage) {
      const lastBlockStr = localStorage.getItem('slot2_last_block_v1');
      const lastBlock = lastBlockStr ? parseInt(lastBlockStr, 10) : 0;
      if (lastBlock > 0) setS2Block(lastBlock);
    }
  } catch {
    /* ignore */
  }

  const saveBlock = () => {
    if (s2DisplayedRound != null) {
      try {
        localStorage.setItem('slot2_last_block_v1', String(s2DisplayedRound));
      } catch {
        /* ignore */
      }
    }
  };
  window.addEventListener('beforeunload', saveBlock);

  renderS2OnchainResults(s2OnchainRows);
  renderS2OnchainPagination();
  remeasureTopInset();
  flushDraw();
}
